/**
 * Auth context. Single source of truth for "who am I" — fetches /auth/me
 * once on mount via React Query, exposes user + claims + isAdmin, and
 * provides login/logout helpers.
 *
 * Pages that require authentication mount inside <RequireAuth>; the SPA
 * redirects to /auth/initiate on null user.
 */

import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useMemo } from 'react';
import { api } from '../lib/api';
import type { MeResponse } from './types';

interface AuthContextValue {
  loading: boolean;
  user: MeResponse['user'];
  claims: MeResponse['claims'] | null;
  isAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    staleTime: 60 * 1000,
    retry: false,
  });

  const login = useCallback(() => {
    window.location.assign('/auth/initiate');
  }, []);

  const logout = useCallback(async () => {
    try {
      const out = await api.post<{ logout_url: string; state: string }>('/auth/logout');
      window.location.assign(out.logout_url);
    } catch {
      window.location.assign('/');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      loading: isLoading,
      user: data?.user ?? null,
      claims: data?.claims ?? null,
      isAdmin: !!data?.isAdmin,
      login,
      logout,
    }),
    [isLoading, data, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function RequireAuth({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const { loading, user } = useAuth();
  if (loading) return <div className="auth-loading">Connecting…</div>;
  if (!user) return <>{fallback}</>;
  return <>{children}</>;
}
